package screens.notification

import androidx.compose.runtime.*
import com.example.readingnotes.di.AppModule
import com.example.readingnotes.domain.model.NotificationType
import com.example.readingnotes.presentation.notificationhistory.NotificationHistoryEvent
import com.example.readingnotes.presentation.notificationhistory.NotificationHistoryViewModel
import com.example.readingnotes.util.DateFormatter
import kotlinx.coroutines.CoroutineScope
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.Instant
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlinx.browser.window
import styles.AppStyleSheet
import styles.property
import styles.transition

@Composable
fun NotificationHistoryScreen(
    onBackClick: () -> Unit,
    appModule: AppModule,
    appScope: CoroutineScope,
    refreshTrigger: Int = 0, // Increment when returning from this screen to refresh
) {
    val viewModel = remember {
        NotificationHistoryViewModel(
            appModule.getNotificationsUseCase,
            appModule.markNotificationAsReadUseCase,
            appModule.markAllNotificationsAsReadUseCase,
            appScope,
        )
    }
    val state by viewModel.state.collectAsState()

    // Refresh when trigger changes (e.g., returning from detail view)
    LaunchedEffect(refreshTrigger) {
        viewModel.onEvent(NotificationHistoryEvent.OnLoadNotifications)
    }

    LaunchedEffect(Unit) {
        viewModel.onEvent(NotificationHistoryEvent.OnLoadNotifications)
    }

    // Clear success message after display
    LaunchedEffect(state.allReadSuccess) {
        if (state.allReadSuccess != null) {
            kotlinx.coroutines.delay(3000)
            viewModel.onEvent(NotificationHistoryEvent.OnClearSuccess)
        }
    }

    Div({ classes(AppStyleSheet.container) }) {
        // Header
        Div({
            classes(AppStyleSheet.header)
            style {
                paddingBottom(16.px)
            }
        }) {
            Button({
                classes(AppStyleSheet.secondaryButton)
                onClick { onBackClick() }
                style { marginRight(16.px) }
            }) {
                Text("← 戻る")
            }

            H1(attrs = {
                style {
                    flexGrow(1)
                }
            }) { Text("通知履歴") }

            // Mark all as read button
            if (state.notifications.any { !it.isRead }) {
                Button({
                    classes(AppStyleSheet.secondaryButton)
                    onClick { viewModel.onEvent(NotificationHistoryEvent.OnMarkAllAsRead) }
                    if (state.isMarkingAllRead) attr("disabled", "true")
                    style {
                        fontSize(12.px)
                        padding(8.px, 12.px)
                        if (state.isMarkingAllRead) {
                            opacity(0.6)
                            cursor("not-allowed")
                        }
                    }
                }) {
                    Text(if (state.isMarkingAllRead) "既読化中..." else "すべて既読")
                }
            }
        }

        // Filter
        Div({
            style {
                marginBottom(16.px)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            Span({ style { fontSize(14.px) } }) {
                Text("未読のみ:")
            }
            ToggleSwitch(
                checked = state.unreadOnly,
                onCheckedChange = {
                    viewModel.onEvent(NotificationHistoryEvent.OnToggleUnreadFilter)
                },
                label = "未読のみ"
            )
        }

        // Success message
        if (state.allReadSuccess != null) {
            Div({
                style {
                    backgroundColor(Color("#4CAF50"))
                    color(Color.white)
                    padding(12.px, 16.px)
                    borderRadius(8.px)
                    marginBottom(16.px)
                    textAlign("center")
                }
            }) {
                Text("${state.allReadSuccess}件の通知を既読にしました")
            }
        }

        // Content
        when {
            state.isLoading && state.notifications.isEmpty() -> {
                Div({ classes(AppStyleSheet.loading) }) { Text("読み込み中...") }
            }
            state.error != null && state.notifications.isEmpty() -> {
                val errorMessage = state.error ?: ""
                Div({ classes(AppStyleSheet.error) }) {
                    Text(errorMessage)
                    Button({
                        classes(AppStyleSheet.primaryButton)
                        style { marginTop(16.px) }
                        onClick { viewModel.onEvent(NotificationHistoryEvent.OnLoadNotifications) }
                    }) { Text("リトライ") }
                }
            }
            state.notifications.isEmpty() -> {
                Div({
                    style {
                        padding(40.px, 20.px)
                        textAlign("center")
                        color(Color("#999"))
                    }
                }) {
                    Text("通知はありません")
                }
            }
            else -> {
                Div({ classes(AppStyleSheet.postList) }) {
                    state.notifications.forEach { notification ->
                        NotificationCard(
                            notification = notification,
                            onClick = {
                                if (!notification.isRead) {
                                    viewModel.onEvent(NotificationHistoryEvent.OnMarkAsRead(notification.id))
                                }
                            }
                        )
                    }

                    // Load more button
                    if (state.hasMore) {
                        if (state.isLoadingMore) {
                            Div({ classes(AppStyleSheet.loading) }) { Text("読み込み中...") }
                        } else {
                            Div({
                                style {
                                    padding(20.px, 0.px)
                                    textAlign("center")
                                }
                            }) {
                                Button({
                                    classes(AppStyleSheet.secondaryButton)
                                    onClick { viewModel.onEvent(NotificationHistoryEvent.OnLoadMore) }
                                }) {
                                    Text("もっと読み込む")
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun NotificationCard(
    notification: com.example.readingnotes.domain.model.Notification,
    onClick: () -> Unit,
) {
    val relativeTime = remember(notification.createdAt) {
        getRelativeTime(notification.createdAt)
    }

    Div({
        classes(AppStyleSheet.postCard)
        attr("role", "article")
        attr("aria-label", "${getNotificationTypeLabel(notification.type)}: ${notification.title}")
        attr("aria-live", "polite")
        attr("tabindex", "0")
        style {
            backgroundColor(if (notification.isRead) Color("#f5f5f5") else Color("#fff"))
            property(
                "border-left",
                "${if (notification.isRead) 4 else 4}px solid ${if (notification.isRead) "#ccc" else "#2196F3"}"
            )
            cursor("pointer")
            if (!notification.isRead) {
                property("font-weight", "bold")
            }
        }
        onClick { onClick() }
    }) {
        // Header
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(8.px)
            }
        }) {
            // Type badge
            Span({
                style {
                    fontSize(12.px)
                    padding(4.px, 8.px)
                    borderRadius(4.px)
                    backgroundColor(getNotificationTypeColor(notification.type))
                    color(Color.white)
                }
                attr("aria-hidden", "true")
            }) {
                Text(getNotificationTypeLabel(notification.type))
            }

            // Time
            Span({
                style {
                    fontSize(12.px)
                    color(Color("#999"))
                }
                attr("aria-label", "${getRelativeTime(notification.createdAt)}")
            }) {
                Text(relativeTime)
            }
        }

        // Title
        H3({
            style {
                fontSize(16.px)
                marginBottom(4.px)
                color(if (notification.isRead) Color("#666") else Color("#000"))
            }
        }) {
            Text(notification.title)
        }

        // Body
        P({
            style {
                fontSize(14.px)
                color(Color("#666"))
                marginBottom(8.px)
            }
        }) {
            Text(notification.body)
        }

        // Related post/comment link
        if (notification.relatedPostId != null) {
            A(
                href = "/post/${notification.relatedPostId}",
                attrs = {
                    onClick {
                        it.preventDefault()
                        // Navigate to post detail (using browser API)
                        val url = "/post/${notification.relatedPostId}"
                        window.history.pushState(null, "", url)
                        window.dispatchEvent(org.w3c.dom.events.Event("popstate"))
                    }
                    attr("aria-label", "投稿 ${notification.relatedPostId}を見る")
                    style {
                        fontSize(12.px)
                        color(Color("#2196F3"))
                        textDecoration("none")
                    }
                }
            ) {
                Text("投稿を見る →")
            }
        }
    }
}

private fun getNotificationTypeLabel(type: NotificationType): String = when (type) {
    NotificationType.NEW_POST -> "新着投稿"
    NotificationType.NEW_COMMENT -> "新着コメント"
    NotificationType.COMMENT_REPLY -> "返信"
    NotificationType.MENTION -> "メンション"
    NotificationType.SYSTEM -> "システム"
}

private fun getNotificationTypeColor(type: NotificationType) = when (type) {
    NotificationType.NEW_POST -> Color("#2196F3")
    NotificationType.NEW_COMMENT -> Color("#4CAF50")
    NotificationType.COMMENT_REPLY -> Color("#FF9800")
    NotificationType.MENTION -> Color("#9C27B0")
    NotificationType.SYSTEM -> Color("#9E9E9E")
}

private fun getRelativeTime(createdAt: kotlinx.datetime.Instant): String {
    val now = Clock.System.now()
    val duration = now.minus(createdAt)
    val minutesAgo = duration.inWholeMinutes

    return when {
        minutesAgo < 1 -> "たった今"
        minutesAgo < 60 -> "${minutesAgo}分前"
        minutesAgo < 1440 -> "${(minutesAgo / 60)}時間前"
        else -> "${(minutesAgo / 1440)}日前"
    }
}

@Composable
private fun ToggleSwitch(
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
    label: String,
) {
    val toggleId = "toggle-$label-${label.hashCode()}"
    Button({
        id(toggleId)
        attr("role", "switch")
        attr("aria-checked", checked.toString())
        attr("aria-label", "$label: ${if (checked) "オン" else "オフ"}")
        style {
            width(48.px)
            height(28.px)
            borderRadius(14.px)
            border(0.px)
            cursor("pointer")
            backgroundColor(if (checked) Color("#4CAF50") else Color("#ccc"))
            property("transition", "background-color 0.2s")
            position(Position.Relative)
        }
        onClick { onCheckedChange(!checked) }
    }) {
        Span({
            style {
                position(Position.Absolute)
                top(2.px)
                left(if (checked) 26.px else 2.px)
                width(24.px)
                height(24.px)
                borderRadius(50.percent)
                backgroundColor(Color.white)
                property("transition", "left 0.2s")
                property("box-shadow", "0 1px 3px rgba(0,0,0,0.3)")
            }
            attr("aria-hidden", "true")
        })
    }
}
